import type { ActivityDTO } from 'src/types/dto';

import {
  isActivityStatusBlockingUnallocate,
  isBookingRequestBlockingScheduleModification,
} from 'src/utils/schedule-modification-lock';

export type UnallocatePermissionGate = {
  canDeleteAny: boolean;
  canDeleteOwn: boolean;
  userId: number | string | null | undefined;
};

export function getActivityProjectOwnerId(row: ActivityDTO): number | undefined {
  const fromOwner = row.standard?.project?.owner?.id;
  if (fromOwner != null) {
    return Number(fromOwner);
  }
  const ownerId = row.standard?.project?.ownerId;
  if (ownerId != null) {
    return Number(ownerId);
  }
  return undefined;
}

export function isActivityProjectOwner(
  row: ActivityDTO,
  userId: number | string | null | undefined
): boolean {
  const ownerId = getActivityProjectOwnerId(row);
  return (
    ownerId != null &&
    userId != null &&
    Number(ownerId) === Number(userId)
  );
}

export function activityHasAllocations(activity: ActivityDTO): boolean {
  return !!(
    activity.appointments?.length ||
    (activity.activityInspectors?.length ?? 0) > 0 ||
    (activity.allocatedInspectors?.length ?? 0) > 0
  );
}

export function hasUnallocatePermission(gate: UnallocatePermissionGate): boolean {
  return gate.canDeleteAny || gate.canDeleteOwn;
}

/**
 * Mirrors DELETE /activities/:id/allocation: DELETE_APPOINTMENTS (not OWN-only)
 * and unconditional project-ownership check on the backend.
 */
export function canUnallocateActivity(
  activity: ActivityDTO,
  gate: UnallocatePermissionGate
): boolean {
  if (
    isBookingRequestBlockingScheduleModification(activity) ||
    isActivityStatusBlockingUnallocate(activity)
  ) {
    return false;
  }
  if (!activityHasAllocations(activity)) {
    return false;
  }
  if (gate.canDeleteAny) {
    return true;
  }
  if (gate.canDeleteOwn) {
    return isActivityProjectOwner(activity, gate.userId);
  }
  return false;
}

/**
 * i18n key suffix under `activities:grid.disabled.*`.
 */
export function getUnallocateDisabledTooltipKey(
  activity: ActivityDTO,
  gate: UnallocatePermissionGate
): string {
  if (
    isBookingRequestBlockingScheduleModification(activity) ||
    isActivityStatusBlockingUnallocate(activity)
  ) {
    return 'grid.disabled.bookingRequestBlocksUnallocate';
  }
  if (canUnallocateActivity(activity, gate)) {
    return '';
  }
  if (!activityHasAllocations(activity)) {
    return '';
  }
  if (!hasUnallocatePermission(gate)) {
    return 'grid.disabled.missingDeleteAppointmentsPermission';
  }
  if (!isActivityProjectOwner(activity, gate.userId)) {
    return 'grid.disabled.notProjectOwner';
  }
  return 'grid.disabled.cannotUnallocate';
}

export function getBatchUnallocateDisabledTooltipKey(
  activities: ActivityDTO[],
  gate: UnallocatePermissionGate
): string {
  const withAllocations = activities.filter(activityHasAllocations);
  if (withAllocations.length === 0) {
    return '';
  }
  if (withAllocations.every((activity) => canUnallocateActivity(activity, gate))) {
    return '';
  }

  for (const activity of withAllocations) {
    const key = getUnallocateDisabledTooltipKey(activity, gate);
    if (key) {
      if (withAllocations.length > 1 && key === 'grid.disabled.notProjectOwner') {
        return 'grid.disabled.batchCannotUnallocate';
      }
      return key;
    }
  }

  return 'grid.disabled.batchCannotUnallocate';
}
